/**
 * Polyfill workaround for CSS 'aspect-ratio' property
 * Given source element/div dimensions and target element/div dimensions (usually the direct parent element),
 * calculate the optimal width/height for the source element/div to maintain its aspect ratio while fitting inside the target element/div.
 *
 * @param sourceWidth - width of the aspect-ratio controlled element
 * @param sourceHeight - height of the aspect-ratio controlled element
 * @param destinationWidth - width of the target container element
 * @param destinationHeight - height of the target container element
 * @returns optimal width and height to maximize size of source while maintaining aspect ratio inside target container
 */
export const getAspectRatioConstraintDims = (sourceWidth, sourceHeight, destinationWidth, destinationHeight) => {
    const isLandscape = sourceWidth > sourceHeight;
    let adjustedHeight;
    let adjustedWidth;
    /**
     *  'aspectRatioConversion' is used to determine a matching dimension given a width or height relative to a known aspect ratio
     *  Using the aspect ratio conversion value, we can change the dimensions of the source element so that it grows or shrinks while maintaining the aspect ratio.
     * The only requirement for this calculation is 2 out of 3 variables: aspectRatioConversion and a targetWidth OR targetHeight.
     *  example:
     *    - aspectRatioConverstion = 1600 (width) / 900 (height) (16:9 landscape aspect ratio)
     *    - aspectRatioConversion === 1.778
     *    - given a height of 900 and 1.778 conversion:
     *      - 1.778 === width / 900
     *      - 900 * 1.778 = width
     *      - width === ~1600
     *    - given a width of 1600 and 1.778 conversion:
     *      - 1.778 === 1600 / height
     *      - 1600 / 1.778 = height
     *      - height === ~900
     * */
    const aspectRatioConversion = isLandscape
        ? sourceWidth / sourceHeight
        : sourceHeight / sourceWidth;
    if (isLandscape) {
        adjustedWidth = destinationWidth;
        adjustedHeight = destinationWidth / aspectRatioConversion;
        // constrain image if it overflows parent width
        if (adjustedWidth > destinationWidth) {
            adjustedWidth = destinationWidth;
            adjustedHeight = destinationWidth * aspectRatioConversion;
        }
        // constrain image if it overflows parent height
        if (adjustedHeight > destinationHeight) {
            adjustedHeight = destinationHeight;
            adjustedWidth = destinationHeight * aspectRatioConversion;
        }
    }
    else {
        adjustedWidth = destinationHeight / aspectRatioConversion;
        adjustedHeight = destinationHeight;
        // constrain image if it overflows parent width
        if (adjustedWidth > destinationWidth) {
            adjustedWidth = destinationWidth;
            adjustedHeight = destinationWidth * aspectRatioConversion;
        }
        // constrain image if it overflows parent height
        if (adjustedHeight > destinationHeight) {
            adjustedHeight = destinationHeight;
            adjustedWidth = destinationHeight * aspectRatioConversion;
        }
    }
    return {
        width: adjustedWidth,
        height: adjustedHeight,
    };
};
//# sourceMappingURL=get_aspect_ratio_constraint_dims.js.map